import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Send, Save, X, Loader2 } from "lucide-react";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Σφάλμα";

export const DraftEditor = ({ x, onChange }) => {
  const [body, setBody] = useState(x.draft_body || "");
  const [busy, setBusy] = useState("");
  const ref = useRef(null);
  const files = x.files || (x.attachments || []).map((n) => ({ id: n, filename: n, legacy: true }));
  const dirty = body !== (x.draft_body || "");

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const { data } = await fn(); onChange(data); if (ok) toast.success(ok); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
    finally { setBusy(""); }
  };
  const save = () => run("save", () => api.patch(`/email-agent/log/${x.id}`, { draft_body: body }), "Το πρόχειρο ενημερώθηκε και στο Outlook");
  const attach = async (file) => {
    if (ref.current) ref.current.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Έως 20MB");
    if (files.some((f) => f.filename === file.name) && !window.confirm(`Το «${file.name}» είναι ήδη συνημμένο. Να προστεθεί ξανά;`)) return;
    if (dirty && !(await save())) return;
    const fd = new FormData();
    fd.append("file", file);
    run("attach", () => api.post(`/email-agent/log/${x.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }), "Επισυνάφθηκε");
  };
  const remove = (fid) => run("rm", () => api.delete(`/email-agent/log/${x.id}/attachments/${fid}`), "Αφαιρέθηκε");
  const send = () => {
    if (!window.confirm(`Αποστολή απάντησης στο ${x.from}${files.length ? ` με ${files.length} συνημμένα` : ""};`)) return;
    run("send", () => api.post(`/email-agent/log/${x.id}/send`, { draft_body: body }), "Στάλθηκε ✓ — αντίγραφο στα Απεσταλμένα");
  };

  return (
    <div className="mt-2 space-y-2" data-testid={`email-agent-draft-editor-${x.id}`}>
      <Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="text-sm" data-testid={`email-agent-draft-body-${x.id}`} />
      <div className="flex flex-wrap gap-1.5" data-testid={`email-agent-draft-files-${x.id}`}>
        {files.map((f) => (
          <span key={f.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
            <Paperclip className="w-3 h-3" /> {f.filename}
            {!f.legacy && (
              <button type="button" onClick={() => remove(f.id)} disabled={!!busy} aria-label={`Αφαίρεση ${f.filename}`} className="ml-0.5 -mr-1 p-1 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`email-agent-draft-remove-${f.id}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={ref} type="file" className="hidden" onChange={(e) => attach(e.target.files?.[0])} data-testid={`email-agent-draft-attach-input-${x.id}`} />
        <Button variant="outline" size="sm" disabled={!!busy} onClick={() => ref.current?.click()} data-testid={`email-agent-draft-attach-btn-${x.id}`}>
          {busy === "attach" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1" />} Επισύναψη αρχείου
        </Button>
        <Button variant="outline" size="sm" disabled={!!busy || !dirty} onClick={save} data-testid={`email-agent-draft-save-btn-${x.id}`}>
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Αποθήκευση
        </Button>
        <Button size="sm" disabled={!!busy || !body.trim()} onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`email-agent-draft-send-btn-${x.id}`}>
          {busy === "send" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Αποστολή
        </Button>
      </div>
    </div>
  );
};
